import { apiService } from './apiService';
import {
  Pipeline,
  Fund,
  CreatePipelineInput,
  UpdatePipelineInput,
  CreateFundInput,
  UpdateFundInput,
  FundraiseStats,
  PipelineShareLink,
  CreatePipelineShareLinkInput,
  PipelineCollaborator,
} from '../types/fundraise';

const BASE_URL = '/api/fundraise';

export const fundraiseService = {
  // ============================================
  // STATS
  // ============================================

  /**
   * Get fundraise overview stats
   */
  async getStats(): Promise<FundraiseStats> {
    const response = await apiService.get(`${BASE_URL}/stats`);
    return response.data.data;
  },

  // ============================================
  // PIPELINES
  // ============================================

  /**
   * Get all pipelines for the current user
   */
  async getPipelines(): Promise<Pipeline[]> {
    const response = await apiService.get(`${BASE_URL}/pipelines`);
    return response.data.data;
  },

  /**
   * Get a single pipeline by ID
   */
  async getPipeline(id: string): Promise<Pipeline> {
    const response = await apiService.get(`${BASE_URL}/pipelines/${id}`);
    return response.data.data;
  },

  /**
   * Create a new pipeline
   */
  async createPipeline(input: CreatePipelineInput): Promise<Pipeline> {
    const response = await apiService.post(`${BASE_URL}/pipelines`, input);
    return response.data.data;
  },

  /**
   * Update an existing pipeline
   */
  async updatePipeline(id: string, input: UpdatePipelineInput): Promise<Pipeline> {
    const response = await apiService.put(`${BASE_URL}/pipelines/${id}`, input);
    return response.data.data;
  },

  /**
   * Delete a pipeline
   */
  async deletePipeline(id: string): Promise<void> {
    await apiService.delete(`${BASE_URL}/pipelines/${id}`);
  },

  /**
   * Toggle pipeline expanded state
   */
  async togglePipelineExpanded(id: string, isExpanded: boolean): Promise<Pipeline> {
    const response = await apiService.put(`${BASE_URL}/pipelines/${id}`, {
      is_expanded: isExpanded,
    });
    return response.data.data;
  },

  // ============================================
  // FUNDS
  // ============================================

  /**
   * Get all funds for a pipeline
   */
  async getFunds(pipelineId: string): Promise<Fund[]> {
    const response = await apiService.get(`${BASE_URL}/pipelines/${pipelineId}/funds`);
    return response.data.data;
  },

  /**
   * Add a fund to a pipeline
   */
  async createFund(input: CreateFundInput): Promise<Fund> {
    const response = await apiService.post(`${BASE_URL}/funds`, input);
    return response.data.data;
  },

  /**
   * Update a fund
   */
  async updateFund(id: string, input: UpdateFundInput): Promise<Fund> {
    const response = await apiService.put(`${BASE_URL}/funds/${id}`, input);
    return response.data.data;
  },

  /**
   * Delete a fund
   */
  async deleteFund(id: string): Promise<void> {
    await apiService.delete(`${BASE_URL}/funds/${id}`);
  },

  /**
   * Move a fund to a different stage
   */
  async moveFund(id: string, stage: string, position: number): Promise<Fund> {
    const response = await apiService.put(`${BASE_URL}/funds/${id}/move`, {
      stage,
      position,
    });
    return response.data.data;
  },

  /**
   * Reorder funds within a stage
   */
  async reorderFunds(pipelineId: string, stage: string, fundIds: string[]): Promise<void> {
    await apiService.put(`${BASE_URL}/pipelines/${pipelineId}/reorder`, {
      stage,
      fund_ids: fundIds,
    });
  },

  // ============================================
  // SHARE LINKS
  // ============================================

  async getShareLinks(pipelineId: string): Promise<PipelineShareLink[]> {
    const response = await apiService.get(
      `${BASE_URL}/pipelines/${pipelineId}/share-links`
    );
    return response.data.data;
  },

  async createShareLink(
    pipelineId: string,
    input?: CreatePipelineShareLinkInput
  ): Promise<PipelineShareLink> {
    const response = await apiService.post(
      `${BASE_URL}/pipelines/${pipelineId}/share-links`,
      input || {}
    );
    return response.data.data;
  },

  async updateShareLink(
    pipelineId: string,
    linkId: string,
    isActive: boolean
  ): Promise<PipelineShareLink> {
    const response = await apiService.put(
      `${BASE_URL}/pipelines/${pipelineId}/share-links/${linkId}`,
      { is_active: isActive }
    );
    return response.data.data;
  },

  async deleteShareLink(pipelineId: string, linkId: string): Promise<void> {
    await apiService.delete(`${BASE_URL}/pipelines/${pipelineId}/share-links/${linkId}`);
  },

  // ============================================
  // COLLABORATORS
  // ============================================

  // Claim a 'collaborate' share-link token after the user has authenticated.
  // The API returns the joined pipeline and signals whether this is a fresh
  // claim (in which case the owner is notified) or a no-op repeat claim.
  async claimCollaboratorInvite(token: string): Promise<{
    pipeline: Pipeline;
    owner_email: string | null;
    owner_name: string | null;
    is_new_membership: boolean;
  }> {
    const response = await apiService.post(`${BASE_URL}/pipelines/claim`, { token });
    return response.data.data;
  },

  async getCollaborators(pipelineId: string): Promise<PipelineCollaborator[]> {
    const response = await apiService.get(
      `${BASE_URL}/pipelines/${pipelineId}/collaborators`
    );
    return response.data.data;
  },

  async removeCollaborator(
    pipelineId: string,
    collaboratorUserId: string
  ): Promise<void> {
    await apiService.delete(
      `${BASE_URL}/pipelines/${pipelineId}/collaborators/${collaboratorUserId}`
    );
  },
};

export default fundraiseService;
